import { ProductImage } from "scrivito/Objs/Product/Components/ProductImage"
import { useEvent } from "shared/lib/useEvent"
import { ProductPosition } from "shared/types/ProductPosition"
import { ProductPositionInstallationReference } from "shared/types/__generated__/graphql"

import { useProductPositionMutations } from "entities/product-positions/api/useProductPositionMutations"
import { useProductPositionLink } from "entities/product-positions/model/useProductPositionLink"
import { useProductPositionProductInformation } from "entities/product-positions/model/useProductPositionProductInformation"
import { ProductPositionFormData } from "entities/product-positions/types/ProductPositionForm"
import { useProductObjLoad } from "entities/products/model/useProductObjLoad"
import { useProjectChildPosition } from "entities/projects/model/useProjectChildPosition"
import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { useProjectRequestMonitor } from "entities/projects/model/useProjectRequestState"
import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { useProductPosition } from "features/product-position-management/model/useProductPosition"
import { ProductPositionFormOptions } from "features/product-position-management/types/ProductPositionFormOptions"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { ProductConfiguratorResult } from "widgets/product-configurator/model/useProductConfiguratorModel"
import { useProductPositionFormModel } from "widgets/product-position/product-position-form/model/useProductPositionFormModel"

import { ProjectPageProductPositionSidebarModel } from "../ProjectPageProductPositionSidebar"

export interface UseProjectPageProductPositionSidebarModelArgs {
  projectID: string
  productPosition: ProductPosition
  formOptions?: ProductPositionFormOptions
}

export function useProjectPageProductPositionSidebarModel({
  projectID,
  productPosition,
  formOptions
}: UseProjectPageProductPositionSidebarModelArgs): ProjectPageProductPositionSidebarModel {
  const { updateProductPosition } = useProductPositionMutations()
  const {
    pendingElementIDs,
    sidebarIsEditing,
    sidebarActiveTab,
    enterSidebarEditing,
    exitSidebarEditing,
    setSidebarActiveTab
  } = useProjectStore(projectID)
  const isPending = pendingElementIDs.includes(productPosition.id)

  const { productInformation, isLoading: isLoadingProductInformation } =
    useProductPositionProductInformation({
      id: productPosition.id,
      skip: isPending
    })

  const { productObj, highestMatchingProductBaseVariantObj, isLoadingObj } =
    useProductObjLoad(productPosition.variantKey)
  const { isUpdatingChildren } = useProjectRequestMonitor({ projectID })
  const { permissions } = useProjectPermissions({ projectID })

  const productPositionModel = useProductPosition({
    productPosition,
    projectID
  })

  const { parent, displayPosition } = useProjectChildPosition({
    child: productPosition,
    projectID
  })

  const handleUpdateProductPosition = useEvent(
    (formData: ProductPositionFormData) => {
      productPositionModel.clarificationRequest.handlers.onSubmitContent?.(
        formData.clarificationRequest?.content
      )
      return updateProductPosition?.({
        projectID,
        parentID: formData.parentID,
        displayPosition: formData.displayPosition,
        productPosition: {
          ...formData.productPosition,
          id: productPosition.id
        }
      })
    }
  )

  const updateConfiguration = useEvent(
    (configuration: ProductConfiguratorResult) => {
      updateProductPosition({
        projectID,
        productPosition: {
          id: productPosition.id,
          variantKey: configuration.variantKey,
          configurationKey: configuration.configurationKey ?? null,
          dimensioning: configuration.externalDimensioningResult ?? null
        }
      })
    }
  )

  const updateInstallationReferences = useEvent(
    (
      productPositionInstallationReferences:
        | ProductPositionInstallationReference[]
        | null
    ) => {
      updateProductPosition({
        projectID,
        productPosition: {
          id: productPosition.id,
          references: productPositionInstallationReferences
        }
      })
    }
  )

  const formModel = useProductPositionFormModel({
    id: "project-sidebar-product-position-form",
    defaultProjectID: projectID,
    productPosition,
    defaultParentID: parent?.id || projectID,
    displayPosition,
    isDefaultEditing: false,
    clarificationRequest: productPositionModel.clarificationRequest,
    onConfigurationChange: updateConfiguration,
    onInstallationReferencesChange: updateInstallationReferences,
    onSubmit: handleUpdateProductPosition,
    formOptions
  })

  const titleLinkProps = useProductPositionLink({
    productPosition
  })

  return {
    image: (
      <ProductImage
        obj={highestMatchingProductBaseVariantObj || productObj}
        isLoading={isLoadingObj}
      />
    ),
    formModel,
    isEditing: sidebarIsEditing,
    enterEditing: enterSidebarEditing,
    exitEditing: exitSidebarEditing,
    sidebarActiveTab,
    setSidebarActiveTab,
    callbacks: productPositionModel.core.handlers,

    isReadonly: !permissions[ProjectPermissionType.EDIT],
    isLoading: isUpdatingChildren || isPending,
    hideControls: !productPositionModel.core.handlers.onEdit,
    titleLinkProps: productObj ? titleLinkProps : undefined,
    isLoadingTitle: isLoadingObj,
    productInformation,
    isLoadingProductInformation,
    externalDimensioningResult: productPosition.dimensioning
  }
}
